import type { InsurerSummary } from '@core/types/api';

// Every logo committed under src/assets/insurers is bundled by Vite and keyed by
// its file name, so adding an insurer's logo is dropping a file in that folder.
const LOGO_MODULES = import.meta.glob('@/assets/insurers/*.{svg,png,jpg,jpeg}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const LOGOS: Record<string, string> = Object.fromEntries(
  Object.entries(LOGO_MODULES).map(([path, url]) => [
    path.split('/').pop()?.replace(/\.(svg|png|jpe?g)$/, '') ?? path,
    url,
  ]),
);

/** The logo file for an insurer, or `undefined` when we do not have one. */
export function logoUrlFor(insurer: Pick<InsurerSummary, 'slug' | 'logo'>): string | undefined {
  return LOGOS[insurer.logo] ?? LOGOS[insurer.slug];
}

/** Initials shown when no logo file exists, so the UI never breaks on a new insurer. */
export function monogram(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/** How big the badge is: a table row wants a small one, a page header a large one. */
export type InsurerLogoSize = 'sm' | 'md';

const SIZES: Record<InsurerLogoSize, string> = {
  sm: 'size-8 rounded-lg',
  md: 'size-14 rounded-xl',
};

export interface InsurerLogoProps {
  insurer: Pick<InsurerSummary, 'slug' | 'logo' | 'name'>;
  size?: InsurerLogoSize;
  className?: string;
}

/**
 * An insurer's logo, falling back to its initials.
 *
 * The name is always rendered next to it by the caller, so the image is
 * decorative and the fallback is not announced twice.
 */
export function InsurerLogo({ insurer, size = 'md', className = '' }: InsurerLogoProps) {
  const url = logoUrlFor(insurer);
  const box = `flex ${SIZES[size]} shrink-0 items-center justify-center overflow-hidden border border-[var(--color-border)] bg-[var(--color-surface)] ${className}`;

  if (!url) {
    return (
      <div className={box} data-testid="insurer-monogram" aria-hidden="true">
        <span
          className={`font-display font-semibold text-[var(--color-accent)] ${size === 'sm' ? 'text-xs' : 'text-lg'}`}
        >
          {monogram(insurer.name)}
        </span>
      </div>
    );
  }

  return (
    <div className={box}>
      <img src={url} alt="" className="size-full object-contain p-1.5" />
    </div>
  );
}
